// The release strip under the branch selector (spec/dashboard.md section 3.3; section 12.2 for releases and pins): which
// environments run this branch's content, which release each serves and how long ago it was built, how many documents
// changed since, and the branch's newest build when nothing serves it. The whole block links to Releases › Environments.
// No timers: the relative times are computed at render, and the data comes from `useBranchReleaseStatus`.
import { Link } from 'react-router';
import { formatInstantWithZone, relativeTime, useTimeZone } from '../ui/time';
import { useGame } from './GameContext';
import { changedSinceText, useBranchReleaseStatus } from './releaseStatus';
import { routes } from './routes';

export function ReleaseStatus({ branch, head }: { branch: string | null; head?: string | null }) {
  const { org, game, offsets } = useGame();
  const [zone] = useTimeZone();
  const s = useBranchReleaseStatus(branch, head);
  if (!branch || s.forbidden) return null;
  if (s.loading) {
    return (
      <div className="release-status" aria-busy="true" aria-label="Loading the release status" data-testid="release-status">
        <span className="skeleton" style={{ width: '70%' }} />
      </div>
    );
  }
  const now = Date.now();
  const at = (iso: string | null) => (
    <span title={iso ? formatInstantWithZone(iso, zone, { offsets }) : undefined}>{relativeTime(iso, now)}</span>
  );
  const latest = s.latest && !s.latest.pinned ? s.latest : null;
  return (
    <Link to={routes.releases(org, game.slug, branch, 'environments')} className="release-status" data-testid="release-status" title="Releases › Environments">
      {s.environments.length === 0 && <span className="rs-line rs-none">Not pinned on any environment</span>}
      {s.environments.map((e) => (
        <span key={e.env.id} className="rs-env-block">
          <span className="rs-line">
            <span className="rs-env">{e.env.slug}</span> <span className="rs-num">#{e.release.number}</span> · {at(e.release.createdAt)}
          </span>
          {e.changed && e.changed.files > 0 && (
            <span className="rs-line rs-ahead" title={`${e.changed.files} file${e.changed.files === 1 ? '' : 's'}; the head is ahead of the pinned release`}>
              {changedSinceText(e.changed)}
            </span>
          )}
        </span>
      ))}
      {latest && (
        <span className="rs-line rs-latest" title={latest.changed && latest.changed.files > 0 ? `the head is ${changedSinceText(latest.changed).replace(' changed since', '')} ahead of this build` : undefined}>
          built <span className="rs-num">#{latest.release.number}</span> · {at(latest.release.createdAt)} · not pinned
        </span>
      )}
    </Link>
  );
}
